<template>
  <v-dialog v-model="dialog" persistent :width="964">
    <AlFormCard :title="title" :subtitle="subtitle">
      <template #error>
        <ErrorAlert
          v-if="error != null"
          :error="error"
          @clearErrors="clearErrors()"
          class="mb-0 rounded"
        />
      </template>

      <template #appendAction>
        <v-btn
          variant="tonal"
          class="rounded"
          size="32"
          @click="closeDialog()"
          icon
          color="default"
          density="comfortable"
        >
          <v-icon size="18" icon="mdi-close" />
        </v-btn>
      </template>

      <div class="d-flex flex-column">
        <v-stepper v-model="wizardStep" elevation="0">
          <v-stepper-header class="elevation-0">
            <v-stepper-item
              title="Detail"
              :value="WIZARD_STEP.Detail"
              :complete="wizardStep > WIZARD_STEP.Detail"
              :color="wizardStep > WIZARD_STEP.Detail ? 'success' : ''"
              class="py-5 pl-0"
              :class="wizardStep > WIZARD_STEP.Detail ? 'text-success' : ''"
              :style="wizardStep > WIZARD_STEP.Detail ? 'opacity: 1;' : ''"
            ></v-stepper-item>

            <v-divider></v-divider>

            <v-stepper-item
              class="py-5 pr-0"
              title="Observations"
              :value="WIZARD_STEP.Questions"
              :complete="wizardStep > WIZARD_STEP.Questions"
              :color="wizardStep > WIZARD_STEP.Questions ? 'success' : ''"
              :class="wizardStep > WIZARD_STEP.Questions ? 'text-success' : ''"
              :style="wizardStep > WIZARD_STEP.Questions ? 'opacity: 1;' : ''"
            ></v-stepper-item>

            <v-divider></v-divider>

            <v-stepper-item
              class="py-5 pr-0"
              title="Additional Details"
              :value="WIZARD_STEP.AdditionalDetails"
            ></v-stepper-item>
          </v-stepper-header>
        </v-stepper>

        <v-window class="mx-0 mb-4" v-model="wizardStep">
          <v-window-item :value="WIZARD_STEP.Detail">
            <ChitDetailForm
              v-model="chitData"
              ref="chitDetailFormRef"
              :error="error"
              :isEdit="isEdit"
            />
          </v-window-item>

          <v-window-item :value="WIZARD_STEP.Questions" v-if="chitData.chit_detail">
            <div>
              <ChitQuestionsForm
                label="Weather and Road Conditions - note any major issues with travel in comment section"
                v-model="chitData.chit_detail.conditions"
                ref="chitConditionsFormRef"
                :type="ChitOptionType.Condition"
                :error="error"
                @clearErrors="clearErrors()"
              />
            </div>
            <div class="mt-8">
              <ChitQuestionsForm
                label="Answer any questions that apply to this chit"
                :type="ChitOptionType.Observation"
                v-model="chitData.chit_detail.observations"
                ref="chitObservationsFormRef"
                :error="error"
                @clearErrors="clearErrors()"
              />
            </div>
          </v-window-item>

          <v-window-item :value="WIZARD_STEP.AdditionalDetails" v-if="chitData.chit_detail">
            <div class="px-4">
              <ChitAdditionalDetailForm
                v-model="chitData.chit_detail"
                :isEdit="isEdit"
                ref="chitAdditionalDetailsFormRef"
                :error="error"
              />
            </div>
          </v-window-item>
        </v-window>
      </div>

      <template #actions>
        <v-btn
          v-if="wizardStep != WIZARD_STEP.Detail"
          class="mr-2"
          variant="outlined"
          :disabled="isChitSitesPending"
          @click="back()"
          >Back</v-btn
        >
        <v-spacer></v-spacer>

        <v-btn
          v-if="wizardStep != WIZARD_STEP.AdditionalDetails"
          color="primary"
          variant="flat"
          @click="next()"
          >Next</v-btn
        >
        <v-btn v-else color="primary" variant="flat" :loading="loading" @click="saveChit()"
          >Save</v-btn
        >
      </template>
    </AlFormCard>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { cloneDeep } from 'lodash'
import { storeToRefs } from 'pinia'

import type { ISystemError } from '@/models/error'
import {
  Chit,
  type IChit,
  type IChitData,
  ChitOptionType,
  type IChitOptionData,
  ChitComment
} from '@/models/chit'

import { diffDeep } from '@/utils/helpers'
import { useCreateChit, useCreateChitComment, useUpdateChit } from '@/composables/chit'
import { useAuthStore } from '@/stores'
import type { ISite, ISiteData } from '@/models/client'
import type { IEmployeeData } from '@/models/employee'

import AlFormCard from '../common/AlFormCard.vue'
import ErrorAlert from '../common/ErrorAlert.vue'
import ChitDetailForm from './ChitDetailForm.vue'
import ChitQuestionsForm from './ChitQuestionsForm.vue'
import ChitAdditionalDetailForm from './ChitAdditionalDetailForm.vue'

interface Props {
  chit?: IChit
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'saved-chit'): void
}>()
const chitData = ref<IChitData>({
  chit_types: [],
  guards: [],
  chit_detail: {
    conditions: [],
    observations: []
  }
} as IChitData)

const dialog = defineModel<boolean>('dialog', { required: true, default: false })

watch(dialog, (value) => {
  if (value) {
    wizardStep.value = WIZARD_STEP.Detail
    clearErrors()
    chitData.value = !props.chit
      ? ({
          chit_types: [],
          chit_detail: {
            conditions: [],
            observations: []
          },
          guards: []
        } as Partial<IChitData>)
      : cloneDeep(props.chit)
  }
})

enum WIZARD_STEP {
  Detail = 1,
  Questions = 2,
  AdditionalDetails = 3
}
const isEdit = computed(() => !!props.chit)

const wizardStep = ref(WIZARD_STEP.Detail)

const title = computed(() =>
  wizardStep.value == WIZARD_STEP.Detail ? 'Chit Detail' : 'Chit Condition Questions'
)
const subtitle = computed(() =>
  wizardStep.value == WIZARD_STEP.Detail
    ? `${isEdit.value ? 'Update' : 'Create'} chit information`
    : 'Answer the following questions to complete the chit creation'
)

function closeDialog() {
  clearErrors()
  dialog.value = false
}

const {
  isPending: isUpdatePending,
  error: updateError,
  mutate: updateMutate,
  reset: updateReset
} = useUpdateChit()
const {
  isPending: isCreatePending,
  error: createError,
  mutate: createMutate,
  reset: createReset
} = useCreateChit()

const localLoading = ref(false)
const loading = computed({
  get: () => {
    return (
      isUpdatePending.value ||
      isCreatePending.value ||
      isCreateChitCommentPending.value ||
      localLoading.value
    )
  },
  set: (value) => {
    localLoading.value = value
  }
})
const localError = ref<ISystemError | null>(null)
const error = computed({
  get: () => {
    return (
      localError.value || updateError.value || createError.value || createChitCommentError.value
    )
  },
  set: (value) => {
    localError.value = value
  }
})

function clearErrors() {
  if (isEdit.value) {
    updateReset()
  } else {
    createReset()
    createChitCommentReset()
  }
  chitDetailFormRef.value?.clearErrors()
  error.value = null
}

async function next() {
  switch (wizardStep.value) {
    case WIZARD_STEP.Detail: {
      // validate chit questions form
      const valid = await chitDetailFormRef.value!.validateForm()
      if (valid) {
        wizardStep.value = WIZARD_STEP.Questions
      }
      break
    }
    case WIZARD_STEP.Questions:
      wizardStep.value = WIZARD_STEP.AdditionalDetails
      break
  }
}

function back() {
  if (wizardStep.value == WIZARD_STEP.Questions) {
    wizardStep.value = WIZARD_STEP.Detail
  } else if (wizardStep.value == WIZARD_STEP.AdditionalDetails) {
    wizardStep.value = WIZARD_STEP.Questions
  }
}

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const {
  isPending: isCreateChitCommentPending,
  error: createChitCommentError,
  reset: createChitCommentReset,
  mutate: createChitComment
} = useCreateChitComment()

async function saveChit() {
  clearErrors()

  const valid = await chitAdditionalDetailsFormRef.value!.validateForm()

  if (valid) {
    // Create a site if client is selected and site is not set
    if (!chitData.value.site && chitData.value.client) {
      localLoading.value = true
      await chitDetailFormRef
        .value!.createClientSite()
        .then((site: ISite) => {
          chitData.value.site = site
        })
        .catch((error: ISystemError) => {
          localError.value = error
        })
        .finally(() => {
          localLoading.value = false
        })
    }

    let chitInstance: IChit | null = null

    // Assign Guard to chitData if not already set
    if (chitData.value.guards?.length == 0) {
      chitData.value.guards.push(currentUser.value!.id!)
    }

    try {
      chitInstance = new Chit(chitData.value)
    } catch (e) {
      error.value = e as ISystemError
    }

    if (chitInstance != null) {
      const saveAction = isEdit.value ? updateMutate : createMutate

      let payload: IChitData = chitInstance

      if (isEdit.value) {
        payload = diffDeep(chitInstance, props.chit, true)
        // use chit_number from props if available
        payload.chit_number = chitInstance.chit_number
      }

      if (payload.site) {
        payload.site =
          typeof payload.site == 'number' ? payload.site : (payload.site as ISiteData).code!
      }

      if (payload.chit_types) {
        payload.chit_types = chitInstance.chit_types!.map((type) => {
          return typeof type == 'number' ? type : (type as IChitOptionData).id!
        })
      }

      if (payload.guards) {
        payload.guards = chitInstance.guards!.map((guard) => {
          return typeof guard == 'number' ? guard : (guard as IEmployeeData).id!
        })
      }

      // Prepare the payload for the API call by mapping conditions and observations
      if (payload.chit_detail?.conditions) {
        payload.chit_detail!.conditions = chitInstance.chit_detail.conditions!.map((condition) => {
          return {
            id: condition.id,
            option: (condition.option as IChitOptionData).id!,
            answer: typeof condition.answer == 'boolean' ? condition.answer : null
          }
        })
      }

      if (payload.chit_detail?.observations) {
        payload.chit_detail!.observations = chitInstance.chit_detail.observations!.map(
          (observation) => {
            return {
              id: observation.id,
              option: (observation.option as IChitOptionData).id!,
              answer: observation.answer
            }
          }
        )
      }

      saveAction(payload, {
        onSuccess: (data) => {
          // if chit has comments, we need to update
          if (!isEdit.value && chitInstance!.chit_detail.comments) {
            const comment = new ChitComment({ text: chitInstance!.chit_detail.comments! })
            createChitComment(
              { data: comment, chitNumber: data.chit_number! },
              {
                onSuccess: () => {
                  emit('saved-chit')
                  closeDialog()
                }
              }
            )
          }
          // set chitData with newly saved data from api
          emit('saved-chit')
          closeDialog()
        }
      })
    }
  }
}

const chitDetailFormRef = ref<typeof ChitDetailForm>()
const chitAdditionalDetailsFormRef = ref<typeof ChitAdditionalDetailForm>()

const isChitSitesPending = computed<boolean>(() => chitDetailFormRef.value?.isPending)
</script>
